import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { colors } from '../../utils/colors';

const TreatmentsSection = styled.section`
  padding: 6rem 1rem;
  background: ${colors.background};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.border}, transparent);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    color: ${colors.textDark};
    margin-bottom: 1rem;
  }

  p {
    color: ${colors.textLight};
    font-size: 1.1rem;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const Card = styled(motion.div)`
  background: ${colors.white};
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px ${colors.shadow};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${colors.primary};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }
`;

const Icon = styled.div`
  font-size: 2.5rem;
  color: ${colors.primary};
  margin-bottom: 1.5rem;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  color: ${colors.textDark};
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: ${colors.textLight};
  line-height: 1.6;
`;

const Badge = styled.span`
  background: ${colors.primaryLight};
  color: ${colors.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.875rem;
  margin-top: 1rem;
  display: inline-block;
`;

export const Treatments = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const treatments = [
    {
      icon: 'fa-solid fa-user',
      title: 'Terapia Individual',
      description: 'Sesiones para adolescentes y adultos enfocadas en tu crecimiento y bienestar emocional.',
      badge: 'Atención Personalizada'
    },
    {
      icon: 'fa-solid fa-brain',
      title: 'Abordaje Cognitivo-Conductual',
      description: 'Técnicas efectivas para modificar patrones de pensamiento y comportamiento.',
      badge: 'Método Probado'
    },
    {
      icon: 'fa-solid fa-eye',
      title: 'Abordaje con EMDR',
      description: 'Terapia especializada para el procesamiento de traumas y experiencias difíciles.',
      badge: 'Técnica Especializada'
    },
    {
      icon: 'fa-solid fa-hands-helping',
      title: 'Acompañamiento en Duelos y Crisis Personales',
      description: 'Apoyo profesional durante procesos de pérdida y momentos difíciles de la vida.',
      badge: 'Acompañamiento Integral'
    },
    {
      icon: 'fa-solid fa-heart',
      title: 'Manejo de Ansiedad',
      description: 'Aprende herramientas prácticas para manejar la ansiedad y el estrés.',
      badge: 'Alta Efectividad'
    },
    {
      icon: 'fa-solid fa-seedling',
      title: 'Desarrollo Personal',
      description: 'Descubre tu potencial y alcanza tus metas personales y profesionales.',
      badge: 'Crecimiento Continuo'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <TreatmentsSection id="servicios">
      <Container>
        <Header>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Servicios Terapéuticos
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Ofrezco una variedad de servicios terapéuticos adaptados a tus necesidades específicas,
            utilizando métodos probados y un enfoque personalizado.
          </motion.p>
        </Header>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Grid>
            {treatments.map((treatment, index) => (
              <Card
                key={index}
                variants={cardVariants}
                whileHover={{ y: -5, boxShadow: '0 15px 40px rgba(0,0,0,0.1)' }}
              >
                <Icon>
                  <i className={treatment.icon}></i>
                </Icon>
                <Title>{treatment.title}</Title>
                <Description>{treatment.description}</Description>
                <Badge>{treatment.badge}</Badge>
              </Card>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </TreatmentsSection>
  );
};
